// Конструктор тура: состояние в браузере (localStorage 'planner'), перенос старой «Моей поездки» ('trip'),
// добавление из шины 'trip:add', сохранение на сервере (ссылка /plan/<id>) с отложенной записью изменений.
// Совместная правка: у тура есть версия; кто сохранил по старой — сливает правки (collab.ts) и сохраняет снова.
// Пока конструктор открыт, раз в несколько секунд спрашиваем версию — правки других участников приходят сами.
import type { Booking, BookingStatus, PlanCreateResult, PlanFx, PlanLive, PlanSaveResult, PlanView } from '@sayohat/shared';
import type { BusEvents } from '../../shared/lib/bus';
import { api } from '../../shared/lib/api';
import { clientId, store } from '../../shared/lib/storage';
import { createStore, useStore } from '../../shared/lib/store';
import { track } from '../../shared/lib/track';
import type { TripItem } from '../../shared/lib/types';
import { toast } from '../../shared/ui/Toast';
import { getProfile, partyOf } from '../profile';
import { mergePlans } from './collab';
import { addItem, addItems, applyBooking, bookedStatus, emptyPlan, fromPlan, migrateTrip, restorePlan, toPlanInput, type LocalPlan, type Rates } from './model';

const KEY = 'planner';
/** Последняя версия тура с сервера — общая точка для слияния правок */
const BASE = 'planner:base';
const LEGACY = 'trip';

export type SyncState = 'local' | 'saving' | 'saved' | 'error';

export interface PlannerState {
  plan: LocalPlan;
  open: boolean;
  sync: SyncState;
  /** курсы ЦБ для пересчёта бюджета (null — не загружены или недоступны) */
  rates: Rates | null;
  /** окно заявки: id тура на сервере или 'local' — текущий тур (сначала сохраним) */
  orderFor: string | null;
  /** окно «Собрать маршрут автоматически»: префикс вида конструктора, который его открыл */
  routeFor: string | null;
  /** «Отменить» после применения маршрута: прежний план и метка нового (пока его не меняли) */
  routeUndo: { prev: LocalPlan; at: string } | null;
  /** сколько человек сейчас в туре (совместная правка); 0 — неизвестно */
  editors: number;
  /** статусы броней позиций (номер брони → статус), загружаются при открытии тура */
  bookings: Record<string, BookingStatus>;
}

/** Первый запуск после обновления: «Моя поездка» переезжает в конструктор */
function initialPlan(): LocalPlan {
  const saved = store.get<unknown>(KEY, null);
  if (saved) return restorePlan(saved);
  const legacy = store.get<unknown>(LEGACY, null);
  const plan = migrateTrip(legacy);
  if (plan.items.length) {
    store.set(KEY, plan);
    try { localStorage.removeItem(LEGACY); } catch { /* нет хранилища */ }
  }
  return plan;
}

export const planner = createStore<PlannerState>({ plan: initialPlan(), open: false, sync: 'local', rates: null, orderFor: null, routeFor: null, routeUndo: null, editors: 0, bookings: {} });
export const usePlanner = () => useStore(planner);

planner.set({ sync: planner.get().plan.id && planner.get().plan.editKey ? 'saved' : 'local' });

let timer: ReturnType<typeof setTimeout> | undefined;

/** Изменить план: запись в браузер и (если тур уже на сервере) отложенное сохранение */
export function setPlan(fn: (p: LocalPlan) => LocalPlan): void {
  const plan = { ...fn(planner.get().plan), updatedAt: new Date().toISOString() };
  store.set(KEY, plan);
  planner.set({ plan });
  if (plan.id && plan.editKey) {
    planner.set({ sync: 'saving' });
    clearTimeout(timer);
    timer = setTimeout(() => { void pushSave(); }, 1200);
  }
}

const baseOf = (): LocalPlan | null => { const b = store.get<unknown>(BASE, null); return b ? restorePlan(b) : null; };

/** Сохранить на сервере по версии; чужая версия новее — слить правки и сохранить ещё раз.
 *  Ответ — updatedAt сохранённого варианта (новые правки после него ещё не сохранены); false — тура на сервере нет */
async function saveRev(): Promise<string | false> {
  for (let attempt = 0; attempt < 3; attempt++) {
    const plan = planner.get().plan;
    if (!plan.id || !plan.editKey) return false;
    const res = await api<PlanSaveResult>('/api/plans/save', {
      method: 'POST', body: { id: plan.id, editKey: plan.editKey, plan: toPlanInput(plan), ...(typeof plan.rev === 'number' ? { baseRev: plan.rev } : {}) },
    });
    const server = fromPlan(res.plan, plan.editKey);
    if (!res.conflict) {
      // Пока сохраняли, могли появиться новые правки — версия обновится, они уйдут следующим сохранением
      const cur = planner.get().plan;
      const next = { ...cur, rev: res.rev };
      store.set(KEY, next);
      store.set(BASE, { ...server, rev: res.rev });
      planner.set({ plan: next });
      return plan.updatedAt;
    }
    const merged = { ...mergePlans(baseOf(), planner.get().plan, server), rev: res.rev, updatedAt: new Date().toISOString() };
    store.set(BASE, { ...server, rev: res.rev });
    store.set(KEY, merged);
    planner.set({ plan: merged });
  }
  throw new Error('Тур сейчас меняют несколько человек — сохраним чуть позже');
}

async function pushSave(): Promise<void> {
  const { plan } = planner.get();
  if (!plan.id || !plan.editKey) return;
  try {
    const saved = await saveRev();
    if (saved && planner.get().plan.updatedAt === saved) planner.set({ sync: 'saved' });
  } catch {
    planner.set({ sync: 'error' });
  }
}

// ---------- Совместная правка: опрос версии ----------

let liveTimer: ReturnType<typeof setInterval> | undefined;
let liveBusy = false;

/** Правки других участников: версия на сервере новее — забираем тур и сливаем со своими несохранёнными правками */
export async function pullLive(): Promise<void> {
  const { plan, sync } = planner.get();
  if (!plan.id || !plan.editKey || liveBusy) return;
  liveBusy = true;
  try {
    const live = await api<PlanLive>(`/api/plans/live?id=${encodeURIComponent(plan.id)}&client=${encodeURIComponent(clientId)}`);
    planner.set({ editors: live.editors });
    if (live.rev <= (plan.rev ?? 0) || sync === 'saving') return;
    const view = await api<PlanView>(`/api/plans?id=${encodeURIComponent(plan.id)}&client=${encodeURIComponent(clientId)}`);
    const cur = planner.get().plan;
    if (cur.id !== plan.id) return;
    const server = fromPlan(view, cur.editKey);
    const dirty = planner.get().sync === 'saving' || planner.get().sync === 'error';
    const next = dirty ? { ...mergePlans(baseOf(), cur, server), rev: view.rev ?? live.rev } : { ...server, rev: view.rev ?? live.rev };
    store.set(BASE, { ...server, rev: view.rev ?? live.rev });
    store.set(KEY, next);
    planner.set({ plan: next });
  } catch {
    /* нет связи — спросим в следующий раз */
  } finally {
    liveBusy = false;
  }
}

/** Опрос раз в 6 секунд, пока вкладка видна и тур сохранён на сервере */
export function startLive(): () => void {
  clearInterval(liveTimer);
  liveTimer = setInterval(() => { if (document.visibilityState === 'visible') void pullLive(); }, 6000);
  void pullLive();
  return () => { clearInterval(liveTimer); planner.set({ editors: 0 }); };
}

/** Вступить в совместную правку по ссылке /plan/<id>#edit=<ключ>: тур становится «моим» в этом браузере */
export function joinPlan(plan: LocalPlan, editKey: string): void {
  const server = { ...plan, editKey };
  store.set(BASE, server);
  store.set(KEY, server);
  planner.set({ plan: server, sync: 'saved', routeUndo: null });
}

/** Ссылка-приглашение к совместной правке (сохраняет тур, если он ещё только в браузере) */
export async function inviteLink(): Promise<string> {
  await savePlan();
  const { plan } = planner.get();
  return `${location.origin}/plan/${plan.id}#edit=${plan.editKey}`;
}

/** Добавить позицию из шины 'trip:add' */
export function addToPlan(item: TripItem): void {
  const r = addItem(planner.get().plan, item);
  if (!r.added) { toast(r.reason === 'full' ? 'В туре уже 200 позиций' : 'Уже в моём туре'); return; }
  track('add_trip', { kind: item.kind });
  setPlan(() => r.plan);
  toast('Добавлено в мой тур');
}

/** Добавить несколько позиций (пакет тура, копия чужого тура) */
export function addManyToPlan(list: TripItem[], meta: Partial<Pick<LocalPlan, 'title' | 'startDate' | 'endDate' | 'travelers'>> = {}): number {
  const cur = planner.get().plan;
  const r = addItems(cur, list);
  if (!r.added) { toast('Уже в моём туре'); return 0; }
  setPlan(() => ({
    ...r.plan,
    title: cur.title || meta.title || '',
    travelers: cur.items.length ? r.plan.travelers : meta.travelers || r.plan.travelers,
    startDate: [r.plan.startDate, meta.startDate].filter(Boolean).sort()[0] || null,
    endDate: [r.plan.endDate, meta.endDate].filter(Boolean).sort().slice(-1)[0] || null,
  }));
  return r.added;
}

/** Бронь на сайте отправлена: отметить позицию тура или добавить новую (force — «В мой тур» на экране брони) */
export function linkBooking(e: BusEvents['booking:done'], force = false): 'linked' | 'added' | 'none' {
  const r = applyBooking(planner.get().plan, e, force);
  if (r.action === 'none') return 'none';
  setPlan(() => r.plan);
  planner.set((s) => ({ bookings: { ...s.bookings, [e.booking.id]: e.booking.status } }));
  return r.action;
}

/** Статусы броней позиций тура: подтверждённая — «забронировано»; брони видны этому браузеру (и тем, где тот же Telegram) */
export async function syncBookings(): Promise<void> {
  const ids = [...new Set(planner.get().plan.items.map((x) => x.bookingId).filter((x): x is string => Boolean(x)))];
  if (!ids.length) return;
  const found: Record<string, BookingStatus> = {};
  await Promise.all(ids.slice(0, 30).map(async (id) => {
    const b = await api<Booking>('/api/bookings/my', { method: 'POST', body: { id, client: clientId } }).catch(() => null);
    if (b) found[id] = b.status;
  }));
  if (!Object.keys(found).length) return;
  planner.set((s) => ({ bookings: { ...s.bookings, ...found } }));
  const plan = planner.get().plan;
  const confirm = plan.items.filter((x) => x.bookingId && found[x.bookingId] && bookedStatus(found[x.bookingId]) && x.status !== 'booked');
  if (confirm.length) setPlan((p) => ({ ...p, items: p.items.map((x) => (confirm.includes(x) ? { ...x, status: 'booked' as const } : x)) }));
}

/** Новый тур: кто едет и валюта — из профиля (если там есть попутчики и валюта), иначе двое взрослых и USD */
export function freshPlan(): LocalPlan {
  const p = getProfile();
  const base = emptyPlan();
  const party = p.travelers?.length ? partyOf(p) : null;
  return {
    ...base,
    travelers: party ? { adults: party.adults, children: party.children + party.infants } : base.travelers,
    budget: { ...base.budget, currency: p.currency || base.budget.currency },
  };
}

/** Сделать тур активным (переключение в «Моих поездках»): он же — точка слияния правок */
export function replaceActive(plan: LocalPlan): void {
  clearTimeout(timer);
  store.set(KEY, plan);
  store.set(BASE, plan);
  planner.set({ plan, sync: plan.id && plan.editKey ? 'saved' : 'local', routeUndo: null, bookings: {}, editors: 0 });
}

export function clearPlan(): void {
  // Ссылка на сохранённый тур остаётся рабочей; новый тур начинается без id
  clearTimeout(timer);
  const plan = freshPlan();
  store.set(KEY, plan);
  planner.set({ plan, sync: 'local' });
}

export const openPlanner = (): void => planner.set({ open: true });
export const closePlanner = (): void => planner.set({ open: false });

/** Сохранить тур на сервере и вернуть ссылку /plan/<id> */
export async function savePlan(): Promise<string> {
  const { plan } = planner.get();
  if (plan.id && plan.editKey) {
    clearTimeout(timer);
    planner.set({ sync: 'saving' });
    const ok = await saveRev()
      .then((x) => x, (err: Error) => {
        // Тура на сервере больше нет (удалён или ключ не подходит) — сохраним как новый
        if (/не найден|Нет прав/.test(err.message)) return false;
        planner.set({ sync: 'error' });
        throw err;
      });
    if (ok) {
      planner.set({ sync: 'saved' });
      return `${location.origin}/plan/${plan.id}`;
    }
    const fresh = { ...planner.get().plan, id: null, editKey: null };
    store.set(KEY, fresh);
    planner.set({ plan: fresh });
  }
  planner.set({ sync: 'saving' });
  const res = await api<PlanCreateResult>('/api/plans', { method: 'POST', body: { ...toPlanInput(plan), client: clientId } })
    .catch((err: Error) => { planner.set({ sync: 'error' }); throw err; });
  const next = { ...planner.get().plan, id: res.plan.id, editKey: res.editKey, rev: res.plan.rev ?? 1 };
  store.set(BASE, { ...fromPlan(res.plan, res.editKey), rev: res.plan.rev ?? 1 });
  store.set(KEY, next);
  planner.set({ plan: next, sync: 'saved' });
  return `${location.origin}${res.url}`;
}

let fxLoading = false;
/** Курсы ЦБ — один раз, когда понадобились */
export function loadRates(): void {
  if (fxLoading || planner.get().rates) return;
  fxLoading = true;
  api<PlanFx>('/api/plans/fx')
    .then((fx) => { if (Object.keys(fx.rates).length) planner.set({ rates: fx.rates }); })
    .catch(() => { /* без курсов — суммы по валютам */ });
}

export const openOrder = (id: string | 'local'): void => planner.set({ orderFor: id });
export const closeOrder = (): void => planner.set({ orderFor: null });

// ---------- Автосборка маршрута ----------

export const openRoute = (prefix: string): void => planner.set({ routeFor: prefix });
export const closeRoute = (): void => planner.set({ routeFor: null });

/** Применить собранный маршрут; прежний план — для одного шага «Отменить» */
export function applyRoute(next: LocalPlan): void {
  const prev = planner.get().plan;
  setPlan(() => next);
  planner.set({ routeUndo: { prev, at: planner.get().plan.updatedAt }, routeFor: null });
}

/** Вернуть план до автосборки (только пока его не меняли после применения) */
export function undoRoute(): void {
  const u = planner.get().routeUndo;
  if (!u || planner.get().plan.updatedAt !== u.at) return;
  // id и ключ — текущие: тур мог успеть сохраниться по ссылке
  const cur = planner.get().plan;
  setPlan(() => ({ ...u.prev, id: cur.id, editKey: cur.editKey }));
  planner.set({ routeUndo: null });
}

export const dropRouteUndo = (): void => planner.set({ routeUndo: null });
